import { useIncentiveClient } from '@/lib/api'
import { MultiSelect } from '@/components/multi-select'
import { useQuery } from '@tanstack/react-query'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/** Mirrors the server-side guard in `IncentiveService.assertGroupChildTypesAllowed`. */
const GIFT_GROUP_CHILD_TYPES = ['intersolve', 'physical']

export default function GiftGroupSettings(props: {
  selected: string[]
  onChange: React.Dispatch<React.SetStateAction<string[]>>
  customerId?: string | undefined | null
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const incentiveClient = useIncentiveClient()

  const { data: incentivesResponse, isLoading } = useQuery({
    queryKey: ['giftGroupIncentives', props.customerId],
    queryFn: async () =>
      await incentiveClient.getByCategory.query({
        query: {
          customerId: props.customerId || undefined,
        },
      }),
    enabled: !!props.customerId,
  })

  const giftGroupItems = useMemo(() => {
    const items = incentivesResponse?.body
    if (!Array.isArray(items)) return []

    return items
      .filter((item: { type: string }) =>
        GIFT_GROUP_CHILD_TYPES.includes(item.type),
      )
      .map(
        (item: {
          id: string
          name: string
          price: number
          picture?: string
          type: string
        }) => ({
          value: item.id,
          label: `${item.name} (€${item.price})`,
          picture: item.picture || undefined,
          badge: t(`incentives.types.${item.type}`),
        }),
      )
  }, [incentivesResponse?.body, t])

  if (isLoading) return <div>{t('loading')}</div>

  if (!props.customerId) {
    return (
      <div className="text-sm">{t('add_incentive.select_customer_first')}</div>
    )
  }

  if (giftGroupItems.length === 0) {
    return (
      <div className="text-sm">{t('add_incentive.no_gift_group_items')}</div>
    )
  }

  return (
    <MultiSelect
      options={giftGroupItems}
      selected={props.selected}
      onChange={props.onChange}
    />
  )
}
